import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { 
  Shield, 
  Globe, 
  Users, 
  BookOpen, 
  Calendar,
  ArrowRight,
  CheckCircle,
  ExternalLink
} from 'lucide-react';

export default function GASIPage() {
  const objectives = [
    "Promote patient safety in aesthetic medicine",
    "Develop structured complication management awareness",
    "Encourage ethical and responsible aesthetic practice",
    "Foster international collaboration among practitioners",
    "Support education in high-risk procedural areas"
  ];

  return (
    <div className="bg-white min-h-screen" data-testid="gasi-page">
      {/* Header Section */}
      <section className="py-16 border-b border-slate-100">
        <div className="imcac-container text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#1B2A4A]/5 rounded-sm mb-6">
            <Shield className="h-4 w-4 text-[#C5A572]" />
            <span className="text-xs font-semibold tracking-wider text-[#1B2A4A] uppercase">Safety Initiative</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-[#1B2A4A] font-heading mb-4">
            GASI
          </h1>
          <p className="text-xl text-[#1B2A4A] font-medium mb-2">
            Global Aesthetic Safety Initiative
          </p>
          <p className="text-slate-600 max-w-2xl mx-auto mb-8">
            Advancing Safety Standards in Aesthetic Medicine Worldwide
          </p>
          
          {/* Quote */}
          <div className="max-w-3xl mx-auto">
            <blockquote className="relative">
              <div className="absolute -top-4 -left-2 text-6xl text-[#C5A572]/20 font-serif">"</div>
              <p className="text-lg md:text-xl text-[#1B2A4A] font-heading italic leading-relaxed px-6">
                Safety and competence are not parallel paths—they are the same road, leading toward global trust in medicine.
              </p>
              <div className="absolute -bottom-4 -right-2 text-6xl text-[#C5A572]/20 font-serif">"</div>
            </blockquote>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-12">
        <div className="imcac-container max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-sm bg-[#1B2A4A] flex items-center justify-center">
              <Globe className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-[#1B2A4A] font-heading">Overview</h2>
          </div>
          
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              GASI is an international academic initiative dedicated to advancing patient safety 
              and clinical excellence in aesthetic medicine. Operating as an open international 
              congress and safety platform under IMCAC, GASI brings together practitioners, 
              educators, and thought leaders to address critical safety concerns in the field.
            </p>
            <p>
              The initiative focuses on complication management, prevention-based practice, and 
              ethical standards in aesthetic procedures. Through structured dialogue and 
              evidence-based education, GASI promotes a culture of safety that transcends 
              geographical boundaries.
            </p>
            <p>
              By fostering international collaboration, GASI aims to establish unified approaches 
              to risk prevention and complication handling, ensuring that practitioners worldwide 
              have access to the knowledge and frameworks necessary for safe aesthetic practice.
            </p>
          </div>
        </div>
      </section>

      <Separator className="max-w-4xl mx-auto" />

      {/* Mission & Objectives */}
      <section className="py-12">
        <div className="imcac-container max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-sm bg-[#1B2A4A] flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-[#1B2A4A] font-heading">Mission & Objectives</h2>
          </div>
          
          <div className="grid gap-3">
            {objectives.map((objective, index) => (
              <div 
                key={index} 
                className="flex items-start gap-3 p-4 bg-slate-50 rounded-sm"
              >
                <CheckCircle className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">{objective}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Separator className="max-w-4xl mx-auto" />

      {/* Editions Section */}
      <section className="py-12">
        <div className="imcac-container max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-sm bg-[#1B2A4A] flex items-center justify-center">
              <Calendar className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-[#1B2A4A] font-heading">Congress Editions</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-5">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Previous Edition</p>
                <h3 className="text-lg font-bold text-[#1B2A4A] mb-1">GASI Bangkok</h3>
                <p className="text-slate-600">12–13 February 2026</p>
                <p className="text-sm text-slate-500 mt-1">Bangkok, Thailand</p>
              </CardContent>
            </Card>
            
            <Card className="border-[#C5A572]/30 bg-[#C5A572]/5">
              <CardContent className="p-5">
                <p className="text-xs font-semibold text-[#C5A572] uppercase tracking-wider mb-2">Upcoming Edition</p>
                <h3 className="text-lg font-bold text-[#1B2A4A] mb-1">GASI Mumbai</h3>
                <p className="text-slate-600">15–16 August 2026</p>
                <p className="text-sm text-slate-500 mt-1">Mumbai, India</p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-6 text-center">
            <Button 
              variant="outline" 
              className="rounded-sm border-[#1B2A4A] text-[#1B2A4A] hover:bg-[#1B2A4A] hover:text-white"
              onClick={() => window.open('#', '_blank')}
              data-testid="visit-gasi-website-btn"
            >
              Visit Official GASI Website <ExternalLink className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      <Separator className="max-w-4xl mx-auto" />

      {/* Educational Scope Clarification */}
      <section className="py-12">
        <div className="imcac-container max-w-4xl">
          <Card className="border-slate-200 bg-slate-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-[#1B2A4A] flex items-center justify-center flex-shrink-0">
                  <Shield className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1B2A4A] mb-2">Educational Scope</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    GASI is an academic educational initiative and does not confer board certification 
                    or regulatory licensure. Participation certificates are issued separately during 
                    congress events as acknowledgment of attendance and engagement.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-12 bg-[#1B2A4A]">
        <div className="imcac-container max-w-4xl text-center">
          <Users className="h-10 w-10 text-[#C5A572] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white font-heading mb-2">
            Join the Global Conversation on Aesthetic Safety
          </h2>
          <p className="text-slate-300 mb-6 max-w-xl mx-auto">
            Be part of an international community committed to advancing safety standards 
            in aesthetic medicine.
          </p>
          
          <div className="flex flex-wrap justify-center gap-3">
            <Button 
              className="bg-white text-[#1B2A4A] hover:bg-slate-100 rounded-sm"
              data-testid="learn-more-btn"
            >
              Learn More <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button 
              className="bg-[#C5A572] text-white hover:bg-[#B8956A] rounded-sm"
              data-testid="register-congress-btn"
            >
              Register for Upcoming Congress
            </Button>
            <Button 
              variant="outline" 
              className="border-white text-white hover:bg-white/10 rounded-sm"
              data-testid="partner-btn"
            >
              Partner with GASI
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
